/**
 * ADR-106 — every tunable number of the top-level swipe, in one place.
 *
 * Every value here was accepted as it is in the device pass on Android Chrome
 * (2026-09-29), with no recalibration: ADR-106, sixth owner-decisions block.
 * The installed TWA and iOS are not yet checked (same block). Change them here
 * and nowhere else; a change is an amendment to that block.
 */
export const SWIPE_NAV = {
  /** Decision 5 / Q4: no gesture starts this close to either viewport edge (px). */
  edgeBandPx: 24,
  /** Decision 6.3: travel before the direction is decided (px) — Classic's SWIPE_LOCK_PX. */
  lockDistancePx: 10,
  /** Decision 6.3: horizontal only if |dx| ≥ ratio · |dy| — Learn's previous-card ratio. */
  lockRatio: 2,
  /** Decision 6.4: commit by distance — this fraction of the viewport width. */
  commitFraction: 0.3,
  /** Decision 6.4: or commit by velocity (px/ms) — Classic's flick constant… */
  commitVelocity: 0.45,
  /** …with at least this much travel (px). */
  commitVelocityMinPx: 48,
  /** Velocity is measured over this trailing window (ms). */
  velocityWindowMs: 100,
  /**
   * Q6: during the drag the moving content follows the finger WITH resistance.
   * The ADR fixed the idea; the device pass accepted the amount, 0.6 of the finger's travel.
   */
  dragGain: 0.6,
  /** Decision 7: at an end of the chain the drag is tracked at about a third of dx. */
  endGain: 1 / 3,
  /** Cancel: the moving content returns to rest (ms). */
  springBackMs: 180,

  // ── The transition (shared axis: ADR-106, fourth owner-decisions block) ──
  // Accepted in the device pass (sixth block).
  //
  // The drag never stops following the finger. With x = finger travel × gain:
  //   shift   = dragResistancePx · ln(1 + x / dragResistancePx)  — grows for as
  //             long as the finger moves, each pixel moving it a little less;
  //   opacity = 1 − (1 − dragMinOpacity) · x / (x + dragFadeScalePx)  — keeps
  //             fading across the whole screen width, toward the floor.
  // (Diagnostic 2026-09-29 §1: a hard cap at 48 px is what read as a wall.)
  /** Scale of the drag's resistance curve (px). At 390 px wide: ~46 px at the commit distance, ~95 px at a full-width drag. */
  dragResistancePx: 60,
  /** Scale of the drag's fade curve (px of finger travel × gain). */
  dragFadeScalePx: 100,
  /** The opacity the drag's fade approaches but never reaches. */
  dragMinOpacity: 0.5,

  // From the frame of release, while the destination renders, the outgoing
  // content keeps leaving — on the compositor, so it keeps moving however busy
  // the main thread is — toward a PARTIAL exit: never gone, so the area is
  // never empty. (Diagnostic §2: the render is 92–97 % of the pause; the stand-in
  // painted a single frame during it.) Sized to outlast the diagnostic's
  // worst common render, ~430 ms at the 6× production stand-in.
  /** Further shift in the swipe's direction while waiting (px)… */
  pendingExitShiftPx: 40,
  /** …toward this opacity (or lower, if the drag already faded further)… */
  pendingExitOpacity: 0.3,
  /** …over this long, decelerating, so it is still moving near the end (ms). */
  pendingExitMs: 600,
  pendingExitEasing: 'cubic-bezier(0.15, 0.6, 0.35, 1)',

  /** When the destination has rendered, the outgoing content continues this much further (px)… */
  exitShiftPx: 48,
  /** …while completing its fade-out over this long (ms). */
  exitMs: 150,
  /** The incoming content starts this far to the side it comes from (px)… */
  enterShiftPx: 48,
  /** …and settles while fading in over this long (ms). It starts with the exit: the two overlap. */
  enterMs: 220,
  /** Easing for all of the above. */
  easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',

  // ── The snapshot neighbour (accepted: ADR-106, fourth owner-decisions block; ADR-107 Q-A) ──
  // The copy made of the outgoing content at commit is kept as that stop's
  // picture. Swiping back toward it, the current content and the picture move
  // together, 1:1, and settle with the flick's velocity. `false` switches the
  // snapshot neighbour off: every swipe is the shared-axis transition above.
  snapshots: true,
  /**
   * Settle after a snapshot drag. Duration = settleSlope × remaining distance ÷
   * release speed, clamped to [settleMinMs, settleMaxMs]; the curve starts at
   * the release speed and eases out (see `settleMotion`). Accepted in the device pass.
   */
  settleSlope: 2.2,
  settleMinMs: 160,
  settleMaxMs: 380,
  /** The settle curve is cubic-bezier(x1, x1 × initial slope, x2, 1). */
  settleControlX1: 0.3,
  settleControlX2: 0.4,
  /** How long past its duration a settle may take before it is treated as done (ms). */
  settleGraceMs: 80,
  /** Tailwind's `md` breakpoint: the gesture is mobile-only (Decision 3.1). */
  mobileQuery: '(max-width: 767px)',
} as const
